import { useCallback, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@nexirt/ui";
import { useStats } from "../lib/stats";
import { SERVICES as CATALOG } from "../content/services";

/**
 * MegaMenu — Services dropdown per Navigation.html mega-menu artboard.
 * Rev 25 Phase 2 Loop D. 680px wide; left column is a 2x3 grid of service
 * tiles; right column shows "Recently shipped" stats (per owner default,
 * replacing the design's case-study card until the first public case
 * study lands).
 *
 * Behavior controlled by the caller: open/onClose props. Caller wires
 * hover/focus into open=true and provides a 200ms grace on hover exit.
 *
 * Rev 25.6.4 (data-honesty fix) — the "Recently shipped" tiles used to
 * be a fully hardcoded STATS array, including a "4.9/5 Client review"
 * line with no backing data source anywhere in the codebase. Systems
 * shipped + avg uptime now come from the same /v1/public/stats source
 * HomeProofStrip uses (shared useStats() hook, lib/stats.ts) so the nav
 * dropdown can never drift from the real numbers. The client-review
 * line is dropped entirely rather than invented a source for it. Since
 * this is a hover-triggered dropdown that can open before the fetch
 * resolves, tiles show a "—" placeholder (never a fabricated number)
 * until `loaded` flips true.
 */

// Nav tiles derive from the shared catalog (content/services.ts) so the
// dropdown always lists every service with the same slug + icon as
// /services and the home grid.
const SERVICES = CATALOG.map((s) => ({
  icon: s.icon,
  title: s.name,
  desc: s.short,
  slug: `/services/${s.slug}`,
}));

// TODO(case-study): swap "Recently shipped" stat block for the design's
// case-study card when the first public client case study ships (owner
// decision per REV_25_PLAN §6).
//
// LCP is the one line here that isn't sourced from /v1/public/stats —
// it's a standing engineering budget rather than a live measurement, so
// it stays static. Systems shipped + avg uptime are filled in from the
// real stats fetch at render time; see the `STATS` array built inside
// MegaMenu() below.

export function MegaMenu({
  open,
  onClose,
  triggerRef,
}: {
  open: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLElement>;
}) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const { stats, loaded } = useStats();

  // Real numbers only once the fetch resolves; never render a
  // fabricated placeholder like the old hardcoded "120+"/"99.98%" in
  // the gap before that. The menu itself still opens instantly on
  // hover — this only gates the two data-backed tiles.
  const STATS: { value: string; label: string }[] = [
    { value: loaded ? `${stats.projects}+` : "—", label: "Systems shipped" },
    { value: loaded ? stats.uptime : "—", label: "Avg uptime" },
    { value: "<2.5s", label: "LCP every page" },
  ];

  // Outside click + Esc close. Focus returns to trigger when closing.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(t) &&
        triggerRef.current &&
        !triggerRef.current.contains(t)
      ) {
        onClose();
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, triggerRef]);

  const onTileClick = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <>
      <style>{MEGA_MENU_STYLES}</style>
      <div
        ref={menuRef}
        role="menu"
        aria-hidden={!open}
        data-mega-menu
        data-open={open}
        className={cn(
          "absolute left-1/2 top-[calc(100%+8px)] z-[var(--nx-z-dropdown)] w-[680px] max-w-[calc(100vw-32px)]",
          "rounded-nx3 border border-nx-line-strong p-4",
          "shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]",
          "backdrop-blur-xl",
          "transition-[opacity,transform] duration-[220ms] ease-nx",
          "grid grid-cols-[1.2fr_1fr] gap-4",
          open
            ? "translate-x-[-50%] translate-y-0 opacity-100"
            : "pointer-events-none translate-x-[-50%] translate-y-[-4px] opacity-0",
        )}
        style={{
          background:
            "linear-gradient(180deg, rgba(17,19,27,0.96), rgba(14,16,24,0.96))",
        }}
      >
        <div>
          <div className="px-1.5 pb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-nx-text-faint">
            What we build
          </div>
          <div className="grid grid-cols-2 gap-0.5">
            {SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={s.slug}
                role="menuitem"
                onClick={onTileClick}
                className={cn(
                  "group flex items-start gap-2.5 rounded-[9px] border-0 bg-transparent p-2.5",
                  "text-left transition-colors duration-150 ease-nx",
                  "hover:bg-white/[0.04]",
                  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nx-accent",
                )}
              >
                <span
                  className={cn(
                    "grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[8px]",
                    "border border-nx-line bg-nx-surface text-nx-accent",
                    "transition-colors group-hover:border-nx-accent/40 group-hover:bg-nx-accent-soft",
                  )}
                >
                  <s.icon className="h-3.5 w-3.5" strokeWidth={1.8} />
                </span>
                <span className="flex flex-col gap-0.5">
                  <span className="text-[13px] font-medium text-nx-text">
                    {s.title}
                  </span>
                  <span className="text-[11.5px] leading-snug text-nx-text-faint">
                    {s.desc}
                  </span>
                </span>
              </Link>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2.5 border-l border-nx-line pl-4">
          <div className="px-0.5 pb-1 font-mono text-[10px] uppercase tracking-[0.18em] text-nx-text-faint">
            Recently shipped
          </div>
          <div className="grid grid-cols-2 gap-2">
            {STATS.map((s, i) => (
              <div
                key={s.label}
                className={cn(
                  "rounded-[8px] border border-nx-line bg-nx-surface px-2.5 py-2",
                  // 3 tiles in a 2-col grid: let the last (static LCP)
                  // tile span the full row instead of leaving a gap.
                  i === STATS.length - 1 && STATS.length % 2 === 1 && "col-span-2",
                )}
              >
                <div className="font-display text-lg font-semibold leading-none tracking-tight text-nx-text">
                  {s.value}
                </div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-nx-text-faint">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
          <Link
            to="/work"
            role="menuitem"
            onClick={onTileClick}
            className={cn(
              "mt-1 inline-flex items-center gap-1.5 text-[12px] text-nx-accent",
              "hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nx-accent",
            )}
          >
            See case studies <ArrowRight className="h-3 w-3" strokeWidth={2.2} />
          </Link>
        </div>
      </div>
    </>
  );
}

const MEGA_MENU_STYLES = `
@media (prefers-reduced-motion: reduce) {
  [data-mega-menu] {
    transition: none !important;
  }
}
`;

export default MegaMenu;
